import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import AuthForm from '@/components/authForm/AuthForm';
import LoggedInAs from '@/components/purchase/LoggedInAs';
import PaymentElementsForm from '@/components/purchase/PaymentElementsForm';
import PurchaseDetails from '@/components/purchase/PurchaseDetails';
import PurchaseForm from '@/components/purchase/PurchaseForm';
import { OnGenericPaymentSubmit } from '@/components/purchase/PurchaseForm/types';
import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useSubPurchase } from '@/hooks/purchase/useSubPurchase';
import { usePurchaseParamsState } from '@/hooks/store';
import genericMessages from '@/messages/messages';
import { User } from '@/store/user/types';

import messages from './messages';
import { Divider, FormTitle, Heading, Wrapper } from './styles';
import { CheckoutFormProps } from './types';

const CheckoutForm = ({ email: _email, onPurchaseSuccess: _onPurchaseSuccess }: CheckoutFormProps) => {
	const { authPrompt } = useAuthPrompt();
	const { plan } = usePurchaseParamsState();
	const { formatMessage } = useIntl();
	const [email, setEmail] = useState(_email ?? undefined);
	const [currentForm, setCurrentForm] = useState<'auth' | 'cc'>(email ? 'cc' : 'auth');
	const submitSubPurchase = useSubPurchase();
	const searchParams = useSearchParams();
	const { isStripeNextEnabled } = useStripeElements();
	const isOrganicTraffic = searchParams?.get('utm_medium') !== 'paid';
	const { logEvent } = useAnalytics();

	const onSubmitPayment: OnGenericPaymentSubmit = async args => {
		return submitSubPurchase({
			...args,
			endpoint: email ? 'subscription/email' : 'subscription',
		});
	};

	const onLogout = () => {
		setEmail('');
		setCurrentForm('auth');
	};

	const onSuccess = () => {
		_onPurchaseSuccess?.();
	};

	const onAuthSuccess = (newUser: User) => {
		setEmail(newUser.email);
		setCurrentForm('cc');
	};

	useEffect(() => {
		if (currentForm === 'cc') {
			logEvent({
				eventName: 'Subscribe : Purchase : Form : Landed',
				eventProps: {
					is_stripe_next: isStripeNextEnabled,
				},
			});
		}
	}, [currentForm, isStripeNextEnabled, logEvent]);

	return (
		<Wrapper id={MAIN_CONTENT_ID}>
			{currentForm === 'auth' && (
				<>
					<Heading>
						{formatMessage(
							authPrompt === 'signup' ? genericMessages.signupTitleMedium : genericMessages.loginTitle,
						)}
					</Heading>
					<AuthForm onAuthSuccess={onAuthSuccess} initialValues={{ email }} />
				</>
			)}
			{currentForm === 'cc' && (
				<>
					<PurchaseDetails isOrganicTraffic={isOrganicTraffic} />
					<Divider />
					{isStripeNextEnabled ? (
						<>
							<LoggedInAs onLogout={onLogout} email={email} />
							<FormTitle>{formatMessage(messages.provideDetails)}</FormTitle>
							<PaymentElementsForm
								endpoint={email ? 'subscription/email' : 'subscription'}
								afterPurchaseSuccess={onSuccess}
								purchaseArgs={email ? { email } : {}}
							/>
						</>
					) : (
						<>
							<FormTitle>{formatMessage(messages.provideDetails)}</FormTitle>
							<PurchaseForm
								hideApplePay={plan === 'lifetime'}
								email={email}
								onLogout={onLogout}
								showLoggedInAs
								onPurchase={onSubmitPayment}
								onSuccess={onSuccess}
							/>
						</>
					)}
				</>
			)}
		</Wrapper>
	);
};

export default CheckoutForm;
